import { Decimal, Money, toIstanbulDate } from "@station/core";
import { z } from "zod";
import { istanbulLocalTime, istanbulWeekday, withinTimeWindow } from "./apply.js";
/**
 * Filo yetkilendirmesi (saf) — ADR 0017 §12–14, `docs/domain/pos-local-api.md` §7.2 kural 1.
 *
 * Kural iki katmanlıdır: carinin VARSAYILANI + aracın GEÇERSİZ KILMASI (`mergeFleetPolicies`; NULL alan üst katmandan
 * gelir). Değerlendirme (`evaluateFleetAuthorization`) sunucuda (gerçek kullanım defteriyle), kasada (kalan limit gösterimi)
 * ve — Go eşleniğiyle — ajanda (çevrimdışı uygulama) AYNI sonucu verir: iki taraf `test-vectors/fleet-authorization.json`
 * ile sınanır.
 *
 * Değişmezler:
 *  - Limit dönemleri MÜŞTERİ takvimidir: İstanbul günü / ayı (iş günü DEĞİL). Kullanım anlık görüntüsü başka bir güne / aya
 *    aitse o dönemin kullanımı sıfır sayılır.
 *  - Limite EŞİT kullanım serbesttir; aşım ihlaldir (`kullanılan + istenen > limit`).
 *  - Litre limiti yalnızca `FUEL` satırlarının miktarını sayar; TL limiti filoya yazılan TUTARI (ödeme) sayar.
 *  - Boş dizi (`[]`) kısıt YOK demektir — `null` ile aynı ("hiçbir ürün izinli değil" anlamsız bir kuraldır).
 *  - Km ve sürücü kartı ZORUNLULUĞU geçersiz kılınamaz (eksik GİRDİDİR, ihlal değil); diğer ihlallerde eylem kuralın
 *    `violationAction`'ıdır: `BLOCK` (müdür de geçemez) ya da `APPROVAL` (müdür PIN'iyle geçer).
 *  - Km geriye gidiyorsa yalnızca UYARI (`ODOMETER_DECREASED`) — sayaç değişimi olabilir.
 */
export const FLEET_VIOLATION_ACTIONS = ["BLOCK", "APPROVAL"];
export const fleetViolationActionSchema = z.enum(FLEET_VIOLATION_ACTIONS);
export const FLEET_VIOLATION_ACTION_LABELS = {
    BLOCK: "Satışı engelle",
    APPROVAL: "Müdür onayına düşür",
};
const quantitySchema = z.string().regex(/^\d{1,15}(\.\d{1,3})?$/, "Miktar, en çok 3 ondalık");
const kurusSchema = z.string().regex(/^\d{1,15}$/, "Kuruş, tam sayı dizge");
const localTimeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Saat SS:DD biçiminde olmalıdır");
/** İzinli zaman penceresi: hafta günleri (0 = Pazar … 6 = Cumartesi; boş = her gün) × `[from, to)` yerel saat. */
export const fleetTimeWindowSchema = z.object({
    weekdays: z.array(z.number().int().min(0).max(6)).max(7).default([]),
    from: localTimeSchema,
    to: localTimeSchema,
});
/** ETKİN kural (katmanlar birleştirilmiş). Ajanın ana verisine bu biçimde iner. */
export const fleetPolicySchema = z.object({
    allowedProductIds: z.array(z.uuid()).max(200).nullable().default(null),
    allowedStationIds: z.array(z.uuid()).max(500).nullable().default(null),
    timeWindows: z.array(fleetTimeWindowSchema).max(14).nullable().default(null),
    dailyLitreLimit: quantitySchema.nullable().default(null),
    monthlyLitreLimit: quantitySchema.nullable().default(null),
    dailyAmountLimit: kurusSchema.nullable().default(null),
    monthlyAmountLimit: kurusSchema.nullable().default(null),
    requireOdometer: z.boolean().default(false),
    requireDriverCard: z.boolean().default(false),
    violationAction: fleetViolationActionSchema.default("BLOCK"),
});
export const FLEET_POLICY_DEFAULTS = fleetPolicySchema.parse({});
/**
 * Katmanları genelden özele birleştirir (`mergeFleetPolicies(cariVarsayılanı, araçKuralı)`): her alan için dolu olan EN
 * ÖZEL katmanın değeri kazanır, hiçbiri doluysa varsayılan (kısıt yok, km / kart istenmez, eylem `BLOCK`).
 */
export function mergeFleetPolicies(...layers) {
    const merged = { ...FLEET_POLICY_DEFAULTS };
    for (const layer of layers) {
        if (!layer)
            continue;
        for (const [key, value] of Object.entries(layer))
            if (value !== null && value !== undefined)
                merged[key] = value;
    }
    return fleetPolicySchema.parse(merged);
}
/** Araç (ya da cari) kullanım anlık görüntüsü — kaynak filo kullanım defteri (+ ajanda senkronlanmamış yerel satışlar). */
export const fleetUsageSchema = z.object({
    /** İstanbul günü, `YYYY-MM-DD`. */
    day: z.iso.date(),
    dayLitres: quantitySchema,
    dayAmount: kurusSchema,
    /** İstanbul ayı, `YYYY-MM`. */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
    monthLitres: quantitySchema,
    monthAmount: kurusSchema,
    lastOdometerKm: z.number().int().nonnegative().nullable().default(null),
});
export const FLEET_VIOLATION_CODES = [
    "STATION_NOT_ALLOWED",
    "TIME_NOT_ALLOWED",
    "PRODUCT_NOT_ALLOWED",
    "DAILY_LITRE_LIMIT",
    "MONTHLY_LITRE_LIMIT",
    "DAILY_AMOUNT_LIMIT",
    "MONTHLY_AMOUNT_LIMIT",
    "ODOMETER_REQUIRED",
    "DRIVER_CARD_REQUIRED",
];
export const FLEET_VIOLATION_LABELS = {
    STATION_NOT_ALLOWED: "İstasyon izinli değil",
    TIME_NOT_ALLOWED: "Saat izinli değil",
    PRODUCT_NOT_ALLOWED: "Ürün izinli değil",
    DAILY_LITRE_LIMIT: "Günlük litre limiti",
    MONTHLY_LITRE_LIMIT: "Aylık litre limiti",
    DAILY_AMOUNT_LIMIT: "Günlük tutar limiti",
    MONTHLY_AMOUNT_LIMIT: "Aylık tutar limiti",
    ODOMETER_REQUIRED: "Kilometre girilmeli",
    DRIVER_CARD_REQUIRED: "Sürücü kartı okutulmalı",
};
/** Eksik girdi — kural eylemi ne olursa olsun geçersiz kılınamaz. */
export const NON_OVERRIDABLE_FLEET_VIOLATIONS = [
    "ODOMETER_REQUIRED",
    "DRIVER_CARD_REQUIRED",
];
export const FLEET_WARNING_CODES = ["ODOMETER_DECREASED"];
const nonNegative = (value) => (value.isNegative() ? Decimal.ZERO : value);
const litres = (value) => value.toFixed(3);
const trLitres = (value) => `${Decimal.from(value).formatTr(3)} L`;
const trMoney = (kurus) => Money.fromMinor(Decimal.from(kurus).toBigInt()).formatTr();
/** Filo yetkilendirmesi. İhlaller DETERMİNİSTİK sırayla döner (`FLEET_VIOLATION_CODES` sırası, ürünler satır sırasıyla). */
export function evaluateFleetAuthorization(policy, usage, request) {
    const at = typeof request.at === "string" ? new Date(request.at) : request.at;
    const day = toIstanbulDate(at);
    const month = day.slice(0, 7);
    const sameDay = usage?.day === day;
    const sameMonth = usage?.month === month;
    const usedDayLitres = Decimal.from(sameDay ? usage.dayLitres : "0");
    const usedDayAmount = Decimal.from(sameDay ? usage.dayAmount : "0");
    const usedMonthLitres = Decimal.from(sameMonth ? usage.monthLitres : "0");
    const usedMonthAmount = Decimal.from(sameMonth ? usage.monthAmount : "0");
    const requestedLitres = Decimal.sum(request.lines.filter((line) => line.kind === "FUEL").map((line) => line.quantity));
    const requestedAmount = Decimal.from(request.amount);
    const violations = [];
    const push = (code, message, meta = {}) => {
        violations.push({ code, message, overridable: !NON_OVERRIDABLE_FLEET_VIOLATIONS.includes(code), meta });
    };
    if (policy.allowedStationIds?.length && !policy.allowedStationIds.includes(request.stationId))
        push("STATION_NOT_ALLOWED", "Bu araç bu istasyonda filo ile alım yapamaz.", {
            stationId: request.stationId,
        });
    if (policy.timeWindows?.length) {
        const weekday = istanbulWeekday(at);
        const time = istanbulLocalTime(at);
        const inside = policy.timeWindows.some((window) => (window.weekdays.length === 0 || window.weekdays.includes(weekday)) &&
            withinTimeWindow(time, window.from, window.to));
        if (!inside)
            push("TIME_NOT_ALLOWED", `Bu saatte (${time}) filo alımına izin yok.`, {
                localTime: time,
                weekday: String(weekday),
            });
    }
    if (policy.allowedProductIds?.length) {
        const seen = new Set();
        for (const line of request.lines) {
            if (policy.allowedProductIds.includes(line.productId) || seen.has(line.productId))
                continue;
            seen.add(line.productId);
            push("PRODUCT_NOT_ALLOWED", "Bu ürün filo kuralında izinli değil.", { productId: line.productId });
        }
    }
    const limitCheck = (code, limit, used, requested, label, format) => {
        if (limit === null || !requested.isPositive())
            return;
        if (used.add(requested).lte(limit))
            return;
        push(code, `${label} aşılıyor: limit ${format(limit)}, kullanılan ${format(used)}, istenen ${format(requested)}.`, { limit, used: used.toString(), requested: requested.toString() });
    };
    limitCheck("DAILY_LITRE_LIMIT", policy.dailyLitreLimit, usedDayLitres, requestedLitres, "Günlük litre limiti", trLitres);
    limitCheck("MONTHLY_LITRE_LIMIT", policy.monthlyLitreLimit, usedMonthLitres, requestedLitres, "Aylık litre limiti", trLitres);
    limitCheck("DAILY_AMOUNT_LIMIT", policy.dailyAmountLimit, usedDayAmount, requestedAmount, "Günlük tutar limiti", trMoney);
    limitCheck("MONTHLY_AMOUNT_LIMIT", policy.monthlyAmountLimit, usedMonthAmount, requestedAmount, "Aylık tutar limiti", trMoney);
    const odometer = request.odometerKm ?? null;
    if (policy.requireOdometer && odometer === null)
        push("ODOMETER_REQUIRED", "Bu araçta kilometre girişi zorunludur.");
    if (policy.requireDriverCard && !request.driverCardPresented)
        push("DRIVER_CARD_REQUIRED", "Bu araçta sürücü kartı okutulması zorunludur.");
    const warnings = [];
    const lastKm = usage?.lastOdometerKm ?? null;
    if (odometer !== null && lastKm !== null && odometer < lastKm)
        warnings.push("ODOMETER_DECREASED");
    const remaining = (limit, used, scale) => limit === null ? null : nonNegative(Decimal.from(limit).sub(used)).toFixed(scale);
    const remainingBefore = {
        dayLitres: remaining(policy.dailyLitreLimit, usedDayLitres, 3),
        monthLitres: remaining(policy.monthlyLitreLimit, usedMonthLitres, 3),
        dayAmount: remaining(policy.dailyAmountLimit, usedDayAmount, 0),
        monthAmount: remaining(policy.monthlyAmountLimit, usedMonthAmount, 0),
    };
    const litreCaps = [remainingBefore.dayLitres, remainingBefore.monthLitres].filter((value) => value !== null);
    const maxLitres = litreCaps.length === 0 ? null : litres(Decimal.min(litreCaps[0], ...litreCaps.slice(1)));
    let action = "NONE";
    if (violations.length > 0)
        action = violations.some((violation) => !violation.overridable) ? "BLOCK" : policy.violationAction;
    return {
        allowed: violations.length === 0,
        action,
        violations,
        warnings,
        usageBefore: {
            day,
            month,
            dayLitres: litres(usedDayLitres),
            dayAmount: usedDayAmount.toFixed(0),
            monthLitres: litres(usedMonthLitres),
            monthAmount: usedMonthAmount.toFixed(0),
        },
        remainingBefore,
        maxLitres,
        requested: { litres: litres(requestedLitres), amount: requestedAmount.toFixed(0) },
    };
}
/**
 * Satıştan SONRAKİ kullanım anlık görüntüsü (ajanın yerel düşümü ve sunucunun ön hesabı için). Dönem devri
 * `evaluateFleetAuthorization`'daki kuralla aynıdır; km yalnızca ileri gider.
 */
export function applyFleetUsage(usage, sale) {
    const at = typeof sale.at === "string" ? new Date(sale.at) : sale.at;
    const day = toIstanbulDate(at);
    const month = day.slice(0, 7);
    const sameDay = usage?.day === day;
    const sameMonth = usage?.month === month;
    const lastKm = usage?.lastOdometerKm ?? null;
    const km = sale.odometerKm ?? null;
    return {
        day,
        dayLitres: Decimal.from(sameDay ? usage.dayLitres : "0")
            .add(sale.litres)
            .toFixed(3),
        dayAmount: Decimal.from(sameDay ? usage.dayAmount : "0")
            .add(sale.amount)
            .toFixed(0),
        month,
        monthLitres: Decimal.from(sameMonth ? usage.monthLitres : "0")
            .add(sale.litres)
            .toFixed(3),
        monthAmount: Decimal.from(sameMonth ? usage.monthAmount : "0")
            .add(sale.amount)
            .toFixed(0),
        lastOdometerKm: km !== null && (lastKm === null || km > lastKm) ? km : lastKm,
    };
}
//# sourceMappingURL=fleet.js.map